import React, { useState, useEffect } from 'react';
import { FileText, Users, DollarSign, Clock, CheckCircle, AlertTriangle, Search, Download, Plus, Edit, Eye, Send, Shield, Calendar, MapPin, X } from 'lucide-react';

const ContractsLegal = () => {
  const [selectedContract, setSelectedContract] = useState('RES-2024-001');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // Mock data pour les statistiques
  const stats = {
    activeContracts: 45,
    inSignature: 12,
    signatureRate: 94,
    pendingDocuments: 8,
    legalCompliance: 100
  };

  // Templates de contrats
  const contractTemplates = [
    {
      id: 'villa-standard',
      name: 'Contrat Villa Standard',
      type: 'Location Villa',
      usage: 156,
      lastUpdated: '2024-09-10'
    },
    {
      id: 'villa-premium',
      name: 'Contrat Villa Premium',
      type: 'Location Luxe',
      usage: 89,
      lastUpdated: '2024-09-08'
    },
    {
      id: 'yacht-charter',
      name: 'Contrat Yacht Charter',
      type: 'Location Yacht',
      usage: 34,
      lastUpdated: '2024-09-12'
    },
    {
      id: 'concierge-services',
      name: 'Contrat Services',
      type: 'Conciergerie',
      usage: 67,
      lastUpdated: '2024-09-05'
    }
  ];

  // Contrat actuel
  const currentContract = {
    id: 'RES-2024-001',
    reference: 'PRIVIA-2024-001-AZURE',
    status: 'En signature',
    client: {
      name: 'Sophie Martin',
      email: 'sophie.martin@email.com',
      phone: '+33 6 12 34 56 78',
      address: '123 Avenue des Champs-Élysées, 75008 Paris'
    },
    property: {
      name: 'Villa Azure',
      address: 'Cannes, Côte d\'Azur',
      type: 'Villa de Luxe',
      capacity: 8
    },
    dates: {
      checkin: '2024-09-15',
      checkout: '2024-09-22',
      duration: 7
    },
    financial: {
      baseAmount: 21000,
      deposit: 6300,
      balance: 14700,
      fees: 1050,
      total: 22050
    },
    workflow: [
      { step: 'Création', status: 'completed', date: '2024-09-10' },
      { step: 'Validation', status: 'completed', date: '2024-09-11' },
      { step: 'Signature', status: 'current', date: null },
      { step: 'Paiement', status: 'pending', date: null },
      { step: 'Actif', status: 'pending', date: null }
    ],
    documents: [
      { name: 'Contrat principal', type: 'PDF', size: '2.3 MB', status: 'signé' },
      { name: 'Conditions générales', type: 'PDF', size: '1.8 MB', status: 'signé' },
      { name: 'Inventaire villa', type: 'PDF', size: '4.2 MB', status: 'en attente' },
      { name: 'Assurance voyage', type: 'PDF', size: '1.1 MB', status: 'en attente' }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'current': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      case 'signé': return 'text-green-600 bg-green-100';
      case 'en attente': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const PreviewModal = () => {
    if (!showPreviewModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800">Aperçu du Contrat</h3>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6">
            <div className="text-center mb-8">
              <img src="/api/placeholder/150/60" alt="Logo PRIVIA" className="mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-800">CONTRAT DE LOCATION</h1>
              <p className="text-gray-600">Villa de Luxe - Séjour Premium</p>
            </div>

            <div className="space-y-6 text-sm">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Article 1 - Parties Contractantes</h3>
                <p>Le présent contrat est conclu entre PRIVIA Luxury Rentals, société par actions simplifiée au capital de 500.000€, immatriculée au RCS de Paris sous le numéro 123 456 789, ci-après dénommée « le Bailleur », et {currentContract.client.name}, ci-après dénommé « le Locataire ».</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Article 2 - Objet du Contrat</h3>
                <p>Le Bailleur met à disposition du Locataire la propriété suivante : {currentContract.property.name}, située à {currentContract.property.address}, pour une période de {currentContract.dates.duration} nuits, du {currentContract.dates.checkin} au {currentContract.dates.checkout}.</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Article 3 - Conditions Financières</h3>
                <p>Le montant total de la location s'élève à {currentContract.financial.total}€ TTC, réparti comme suit :</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Montant de base : {currentContract.financial.baseAmount}€</li>
                  <li>Frais de service : {currentContract.financial.fees}€</li>
                  <li>Acompte (30%) : {currentContract.financial.deposit}€</li>
                  <li>Solde : {currentContract.financial.balance}€</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Article 4 - Modalités de Paiement</h3>
                <p>L'acompte de {currentContract.financial.deposit}€ est exigible à la signature du présent contrat. Le solde de {currentContract.financial.balance}€ devra être réglé 30 jours avant la date d'arrivée.</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Article 5 - Conditions d'Annulation</h3>
                <p>En cas d'annulation par le Locataire, les conditions suivantes s'appliquent selon la période d'annulation :</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Plus de 60 jours avant l'arrivée : remboursement intégral</li>
                  <li>Entre 60 et 30 jours : retenue de 50% de l'acompte</li>
                  <li>Moins de 30 jours : aucun remboursement</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                Ce document constitue un aperçu du contrat. La version complète sera générée lors de la finalisation.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="contracts-legal">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Contrats & Légal</h1>
            <p className="text-gray-600 mt-1">Gestion complète des contrats et documents légaux</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher un contrat..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Download size={18} />
              <span>Rapports</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Contrat</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <FileText className="text-blue-600" size={24} />
              <span className="text-sm font-medium text-green-600">+8.2%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.activeContracts}</div>
            <div className="text-sm text-gray-600">Contrats Actifs</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Edit className="text-orange-600" size={24} />
              <span className="text-sm font-medium text-orange-600">Urgent</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.inSignature}</div>
            <div className="text-sm text-gray-600">En Signature</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">+2.1%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.signatureRate}%</div>
            <div className="text-sm text-gray-600">Taux de Signature</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-yellow-600" size={24} />
              <span className="text-sm font-medium text-yellow-600">-5.3%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.pendingDocuments}</div>
            <div className="text-sm text-gray-600">Documents en Attente</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Shield className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">Conforme</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.legalCompliance}%</div>
            <div className="text-sm text-gray-600">Conformité Légale</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Templates Sidebar */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Modèles de Contrats</h3>
            
            <div className="space-y-3">
              {contractTemplates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer">
                  <h4 className="font-medium text-gray-800 text-sm">{template.name}</h4>
                  <p className="text-xs text-gray-600 mb-2">{template.type}</p>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">Utilisé {template.usage}x</span>
                    <span className="text-gray-500">{template.lastUpdated}</span>
                  </div>
                </div>
              ))}
            </div>

            <button className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700">
              <Plus size={16} />
              <span>Nouveau Modèle</span>
            </button>
          </div>

          {/* Contenu Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Détails du Contrat */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{currentContract.reference}</h3>
                  <p className="text-sm text-gray-600">Contrat de location - {currentContract.id}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor('current')}`}>
                    {currentContract.status}
                  </span>
                  <button
                    onClick={() => setShowPreviewModal(true)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>

              {/* Onglets */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                  {[
                    { id: 'info', label: 'Informations' },
                    { id: 'parties', label: 'Parties' },
                    { id: 'property', label: 'Propriété' },
                    { id: 'financial', label: 'Financier' },
                    { id: 'clauses', label: 'Clauses' },
                    { id: 'documents', label: 'Documents' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-yellow-500 text-yellow-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Contenu des onglets */}
              <div className="tab-content">
                {activeTab === 'info' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Référence Contrat</label>
                        <input
                          type="text"
                          value={currentContract.reference}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                          <option>En signature</option>
                          <option>Brouillon</option>
                          <option>Signé</option>
                          <option>Actif</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Check-in</label>
                        <input
                          type="date"
                          value={currentContract.dates.checkin}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Check-out</label>
                        <input
                          type="date"
                          value={currentContract.dates.checkout}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Durée</label>
                        <input
                          type="text"
                          value={`${currentContract.dates.duration} nuits`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'parties' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-3">Client</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                          <input
                            type="text"
                            value={currentContract.client.name}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={currentContract.client.email}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                          <input
                            type="text"
                            value={currentContract.client.phone}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                        <textarea
                          value={currentContract.client.address}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          rows="2"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'property' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la propriété</label>
                        <input
                          type="text"
                          value={currentContract.property.name}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <input
                          type="text"
                          value={currentContract.property.type}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
                        <input
                          type="text"
                          value={currentContract.property.address}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Capacité</label>
                        <input
                          type="number"
                          value={currentContract.property.capacity}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'financial' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Montant de base (€)</label>
                        <input
                          type="number"
                          value={currentContract.financial.baseAmount}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frais de service (€)</label>
                        <input
                          type="number"
                          value={currentContract.financial.fees}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Acompte (€)</label>
                        <input
                          type="number"
                          value={currentContract.financial.deposit}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Solde (€)</label>
                        <input
                          type="number"
                          value={currentContract.financial.balance}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-800">TOTAL TTC</span>
                        <span className="text-xl font-bold text-yellow-600">€{currentContract.financial.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'documents' && (
                  <div className="space-y-4">
                    {currentContract.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileText className="text-gray-400" size={20} />
                          <div>
                            <h4 className="font-medium text-gray-800">{doc.name}</h4>
                            <p className="text-sm text-gray-600">{doc.type} • {doc.size}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(doc.status)}`}>
                            {doc.status}
                          </span>
                          <button className="text-gray-400 hover:text-gray-600">
                            <Download size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Panneau de Signature */}
          <div className="space-y-6">
            {/* Workflow de Signature */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Workflow de Signature</h3>
              
              <div className="space-y-4">
                {currentContract.workflow.map((step, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      step.status === 'completed' ? 'bg-green-100 text-green-600' :
                      step.status === 'current' ? 'bg-blue-100 text-blue-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {step.status === 'completed' ? (
                        <CheckCircle size={16} />
                      ) : step.status === 'current' ? (
                        <Clock size={16} />
                      ) : (
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{step.step}</h4>
                      {step.date && (
                        <p className="text-sm text-gray-600">{step.date}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 space-y-3">
                <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Send size={18} />
                  <span>Envoyer pour Signature</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Users size={18} />
                  <span>Relancer le Client</span>
                </button>
              </div>
            </div>

            {/* Sécurité & Conformité */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Sécurité & Conformité</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Certification eIDAS</span>
                  <CheckCircle className="text-green-600" size={16} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Horodatage qualifié</span>
                  <CheckCircle className="text-green-600" size={16} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Archivage légal 10 ans</span>
                  <CheckCircle className="text-green-600" size={16} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Conformité RGPD</span>
                  <CheckCircle className="text-green-600" size={16} />
                </div>
              </div>

              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  <Shield className="inline mr-1" size={14} />
                  Contrat conforme aux réglementations françaises et européennes
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal />
    </div>
  );
};

export default ContractsLegal;